import { Box, Stack, Text } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';

import { useCodingScreen } from '../../interface/context';
import type { SlotProps } from '../../interface/slotProps';
import type { TransactionSectionsData } from '../../interface/transactionCodes';
import type { TransactionItem, TransactionsData } from '../../interface/transactions';
import { INK_MUTED, TX_ERROR_INK, TX_GAP, TX_LIST_PAD, TX_MESSAGE_SIZE } from '../../styles/tokens';
import { TransactionContainer } from '../TransactionContainer';
import { TransactionSections } from '../TransactionSections';
import { applyMove, dropWithinInvoice, movesFor } from './reorder';

export function isTransactionsData(value: unknown): value is TransactionsData {
  if (value == null || typeof value !== 'object') return false;
  const candidate = value as { status?: unknown; transactions?: unknown };
  return (
    (candidate.status === 'loading' || candidate.status === 'ready' || candidate.status === 'failed') &&
    Array.isArray(candidate.transactions)
  );
}

const Message: React.FC<{ readonly children: string }> = ({ children }) => (
  <Text fontSize={TX_MESSAGE_SIZE} color={INK_MUTED}>
    {children}
  </Text>
);

const EMPTY: readonly TransactionItem[] = [];

const sectionsOf = (transaction: TransactionItem): TransactionSectionsData | undefined =>
  transaction.modifiers && transaction.diagnoses && transaction.providers
    ? {
        id: transaction.id,
        procedure: transaction.procedure,
        modifiers: transaction.modifiers,
        diagnoses: transaction.diagnoses,
        providers: transaction.providers
      }
    : undefined;

const sideFor = (element: HTMLElement, clientY: number): 'before' | 'after' => {
  const rect = element.getBoundingClientRect();
  return clientY < rect.top + rect.height / 2 ? 'before' : 'after';
};

export const TransactionList: React.FC<SlotProps> = ({ value }) => {
  const { api } = useCodingScreen();
  const data = isTransactionsData(value) ? value : undefined;
  const items = data?.status === 'ready' ? data.transactions : EMPTY;
  const [order, setOrder] = useState<readonly string[] | undefined>(undefined);
  const [dragging, setDragging] = useState<string | undefined>(undefined);
  const [target, setTarget] = useState<{ id: string; side: 'before' | 'after' } | undefined>(undefined);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  const scope = useRef(0);
  const reorderTransactions = api?.reorderTransactions;
  const caseScope = data?.scope;
  const revision = data?.revision;
  const signature = items.map((transaction) => `${transaction.invoiceId}/${transaction.id}`).join('\n');

  useEffect(() => {
    scope.current += 1;
    setOrder(undefined);
    setPending(false);
    setError(false);
    setDragging(undefined);
    setTarget(undefined);
  }, [caseScope]);

  useEffect(() => {
    setOrder(undefined);
    setError(false);
  }, [signature, revision]);

  const byId = new Map(items.map((transaction) => [transaction.id, transaction]));
  const shown: readonly TransactionItem[] = order
    ? [
        ...order.map((id) => byId.get(id)).filter((transaction): transaction is TransactionItem => transaction !== undefined),
        ...items.filter((transaction) => !order.includes(transaction.id))
      ]
    : items;
  const canReorder = typeof reorderTransactions === 'function' && data?.permission === 'allowed';
  const invoiceOf = (id: string | undefined) => shown.find((transaction) => transaction.id === id)?.invoiceId;

  const persist = async (next: readonly TransactionItem[], invoiceId: string) => {
    if (!reorderTransactions || pending) return;
    const startedIn = scope.current;
    setOrder(next.map((transaction) => transaction.id));
    setError(false);
    setPending(true);
    try {
      await reorderTransactions({
        invoiceId,
        orderedTransactionIds: next
          .filter((transaction) => transaction.invoiceId === invoiceId)
          .map((transaction) => transaction.id)
      });
    } catch {
      if (scope.current === startedIn) {
        setOrder(undefined);
        setError(true);
      }
    } finally {
      if (scope.current === startedIn) setPending(false);
    }
  };

  const onDrop = (targetId: string, event: React.DragEvent) => {
    event.preventDefault();
    const draggedId = dragging ?? event.dataTransfer?.getData('text/plain');
    setDragging(undefined);
    setTarget(undefined);
    if (!canReorder || !draggedId) return;
    const next = dropWithinInvoice(shown, draggedId, targetId, sideFor(event.currentTarget as HTMLElement, event.clientY));
    const invoiceId = invoiceOf(draggedId);
    if (next && invoiceId !== undefined) void persist(next, invoiceId);
  };

  let body: React.ReactNode;
  if (data === undefined || data.status === 'loading') {
    body = <Message>Loading transactions…</Message>;
  } else if (data.status === 'failed') {
    body = <Message>Transactions could not be loaded</Message>;
  } else if (shown.length === 0) {
    body = <Message>No transactions</Message>;
  } else {
    body = (
      <Stack spacing={TX_GAP}>
        {shown.map((transaction, index) => {
          const moves = canReorder ? movesFor(shown, transaction.id) : [];
          const sections = sectionsOf(transaction);
          return (
            <TransactionContainer
              key={transaction.id}
              index={index}
              transaction={transaction}
              grip={moves.length > 0 ? 'active' : 'inert'}
              moves={moves}
              onMove={(move) => {
                const next = applyMove(shown, transaction.id, move);
                if (next) void persist(next, transaction.invoiceId);
              }}
              dropIndicator={target?.id === transaction.id ? target.side : undefined}
              onGripDragStart={(event) => {
                event.dataTransfer?.setData('text/plain', transaction.id);
                if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
                setDragging(transaction.id);
              }}
              onDragEnd={() => {
                setDragging(undefined);
                setTarget(undefined);
              }}
              onDragOver={(event) => {
                if (!dragging || dragging === transaction.id || invoiceOf(dragging) !== transaction.invoiceId) return;
                event.preventDefault();
                setTarget({ id: transaction.id, side: sideFor(event.currentTarget as HTMLElement, event.clientY) });
              }}
              onDrop={(event) => onDrop(transaction.id, event)}
            >
              {sections && <TransactionSections transaction={sections} reorderable={data?.permission === 'allowed'} />}
            </TransactionContainer>
          );
        })}
        {error && (
          <Text role="alert" fontSize={TX_MESSAGE_SIZE} color={TX_ERROR_INK}>
            Couldn't reorder. Try again.
          </Text>
        )}
      </Stack>
    );
  }
  return (
    <Box role="region" aria-label="Transactions" data-testid="transaction-list" p={TX_LIST_PAD}>
      {body}
    </Box>
  );
};
